import { useCallback } from "react";

import { useGetCropMixComposerSettings, useMutateCropMixComposerSettings, useTranslation } from "utils";

import { CROP_ROTATION, MutateCropMixComposerPreferencesRequest } from "models";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { getNextAvailableMixName } from "pages/app/crop-mix-composer/utils/cropMixComposer";

export const useCreateDraftMix = () => {
  const { t } = useTranslation("cropMixComposer");
  const { data: settingsCMC } = useGetCropMixComposerSettings();
  const { mutateAsync: mutateAsyncCropMixComposerSettings } = useMutateCropMixComposerSettings();
  const {
    cropRotationFamiliesToExclude,
    drought,
    isCreatingDraftMixRef,
    legumeExclusionConfirmation,
    mainCropSettings,
    soilPh,
    soilType,
    setIsDraftMix,
    setIsNewCropMix,
  } = useCropMixComposerContext();

  const createDraftMix = useCallback(
    async (name?: string) => {
      if (isCreatingDraftMixRef.current) return undefined;
      isCreatingDraftMixRef.current = true;

      const requestBody: MutateCropMixComposerPreferencesRequest = {
        name: name ?? getNextAvailableMixName(t("cropMixSelectionModal.myMix"), settingsCMC),
        recommendation: {
          settings: {
            soilType,
            soilPh,
            isDrought: drought,
            cropRotationFamiliesToExclude:
              cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === true
                ? cropRotationFamiliesToExclude.filter((family) => family !== CROP_ROTATION.FABACEAE)
                : cropRotationFamiliesToExclude,
          },
          mainCropSettings,
        },
      };

      try {
        const newMix = await mutateAsyncCropMixComposerSettings({ data: requestBody });
        setIsNewCropMix(true);
        setIsDraftMix(false);
        return newMix;
      } finally {
        isCreatingDraftMixRef.current = false;
      }
    },
    [
      cropRotationFamiliesToExclude,
      drought,
      isCreatingDraftMixRef,
      legumeExclusionConfirmation,
      mainCropSettings,
      mutateAsyncCropMixComposerSettings,
      setIsDraftMix,
      setIsNewCropMix,
      settingsCMC,
      soilPh,
      soilType,
      t,
    ]
  );

  return { createDraftMix };
};
